<!--suppress CheckEmptyScriptTag -->
<template>
  <q-dialog
    @show="onOpen()"
    @hide="onDialogHide()"
    persistent
    :value="isOpen" >
    <q-card style="min-width: 500px">

      <q-btn dense flat size="sm" icon="close" color="red" class="float-right q-ma-sm" style="z-index: 9999" @click="hide" />
      <div class="text-h3 q-pa-md text-center">Checkout</div>

      <q-card-section>
        <div class="q-gutter-md">
          <div class="row text-h6 ">
            Shipping Address
          </div>
          <shipping-details v-model="address" ref="shippingDetails"/>

          <div class="row">
            <q-input label="Voucher Code" v-model="voucherCode" class="col" :rules="[ applyVoucher ]" debounce="1000" clearable/>
          </div>
          <div v-if="voucherDiscount" class="row text-h6 q-mt-none">
            Discount Applied: ${{Number(voucherDiscount).toFixed(2)}}
          </div>
          <div class="row">
            <label for="card-element" class="col-auto q-pb-sm self-end text-h6" v-if="!initialLoading">
              Payment Information
            </label>
            <div id="card-element" class="full-width">
              <!-- A Stripe Element will be inserted here. -->
            </div>
          </div>
          <div class="row text-red q-pb-md">
              {{errorMessage}}
          </div>
        </div>
        <div class="row">
          <q-btn class="full-width" color="primary"
                 :loading="isProcessing"
                 :disabled="initialLoading || isProcessing || voucherInvalid" @click="pay">
            Pay ${{Number(totalCostWithDiscount).toFixed(2)}}
          </q-btn>
        </div>
        <q-inner-loading :showing="initialLoading">
          <q-spinner-dots size="50px" color="primary"/>
        </q-inner-loading>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import gql from 'graphql-tag'
import store from '../store'
import ShippingDetails from './ui/ShippingDetails'
// eslint-disable-next-line no-undef
const stripe = Stripe(store.state.auth.stripePublishableKey)
const elements = stripe.elements({
  fonts: [{
    cssSrc: 'https://fonts.googleapis.com/css?family=Raleway&display=swap'
  }]
})

export default {
  name: 'StripeDialog',
  components: { ShippingDetails },
  props: ['cartId', 'totalCost', 'apollo'],
  data () {
    return {
      address: {},
      isOpen: false,
      isProcessing: false,
      initialLoading: true,
      stripe: null,
      card: null,
      errorMessage: null,
      backendAddress: (process.env.VUE_APP_GRAPHQL_HTTP || 'http://localhost:4000/graphql').replace('/graphql', ''),
      voucherCode: '',
      voucherDiscount: 0
    }
  },
  computed: {
    totalCostWithDiscount () {
      return this.totalCost - this.voucherDiscount
    },
    voucherInvalid () {
      return !!this.voucherCode && !this.voucherDiscount
    }
  },
  methods: {
    onOpen () {
      this.card.mount('#card-element')
      this.card.on('ready', () => { this.initialLoading = false })
    },
    show () {
      this.isOpen = true
    },
    hide () {
      this.isOpen = false
    },
    onDialogHide () {
      this.card.unmount()
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide')
    },
    startProcessing () {
      this.isProcessing = true
    },
    finishProcessing () {
      this.isProcessing = false
    },
    clearError () {
      this.errorMessage = ''
    },
    showError (errorMessage) {
      this.errorMessage = errorMessage
      this.finishProcessing()
    },
    async orderComplete (clientSecret, orderId) {
      this.isOpen = false
      this.$emit('ok', {
        orderId
      })
      this.finishProcessing()
    },
    async handleAction (clientSecret) {
      const data = await stripe.handleCardAction(clientSecret)
      if (data.error) {
        this.showError('Your card was not authenticated, please try again')
        return
      }

      if (data.paymentIntent.status !== 'requires_confirmation') {
        // Shouldn't really be able to get here.
        this.showError(data)
        return
      }

      let response = await this.apollo.mutate({
        mutation: gql`
            mutation payForRetailOrder($cartId: ID!, $paymentInfo: PaymentInfoInput!) {
              payForRetailOrder (cartId: $cartId, paymentInfo: $paymentInfo) {
                order { id },
                paymentDetails {
                    clientSecret,
                    requiresAction
                }
              }
            }
          `,
        variables: {
          cartId: this.cartId,
          paymentInfo: {
            paymentIntentId: data.paymentIntent.id,
            address: this.address
          }
        }
      })

      const responseData = response.data.payForRetailOrder
      this.orderComplete(responseData.clientSecret, responseData.order.id)
    },
    async applyVoucher () {
      if (this.voucherCode == null || this.voucherCode === '') {
        this.voucherDiscount = 0
        return true
      }
      try {
        let response = await this.apollo.query({
          query: gql`
            query voucherDiscountOnCart($cartId: ID!, $voucherCode: String!) {
              voucherDiscountOnCart(voucherCode: $voucherCode, cartId: $cartId)
            }
          `,
          variables: {
            cartId: this.cartId,
            voucherCode: this.voucherCode
          }
        })
        this.voucherDiscount = response.data.voucherDiscountOnCart
        return true
      } catch (ex) {
        this.voucherDiscount = 0
        if (ex.graphQLErrors && ex.graphQLErrors.length > 0) {
          return ex.graphQLErrors[0].message
        }
        return ex.message
      }
    },
    asyncDialog (config) {
      return new Promise((resolve, reject) => {
        this.$q.dialog(config).onOk(resolve).onCancel(reject)
      })
    },
    shippingDetailsModified () {
      let accountAddress = store.getters['auth/getAccountAddress']
      return !Object.entries(this.address)
        .every(([key, value]) => accountAddress?.[key] === value)
    },
    async saveAddress (address) {
      this.updateAccount({ addresses: [address] })
    },
    updateAccount (input) {
      store.dispatch('auth/updateCurrentAccount', input).catch(err => {
        this.$q.notify({
          message: 'Error updating!',
          color: 'negative',
          icon: 'report_problem'
        })
      })
    },
    async pay () {
      this.clearError()
      this.startProcessing()
      const shippingDetailsValid = await this.$refs.shippingDetails.validate()
      if (!shippingDetailsValid) {
        this.finishProcessing()
        return
      }

      try {
        const result = await stripe.createPaymentMethod('card', this.card)
        if (result.error) {
          this.showError(result.error.message)
          return
        }

        if (this.shippingDetailsModified()) {
          try {
            await this.asyncDialog({
              title: 'Update Shipping Address',
              message: `Looks like you updated your shipping details.  Do you want to save the changes to your account?`,
              persistent: true,
              ok: { label: 'Save' },
              cancel: { label: 'Continue without saving', flat: true }
            })
            this.saveAddress(this.address)
          } catch {
            //  Don't save
          }
        }

        let response = await this.apollo.mutate({
          mutation: gql`
            mutation payForRetailOrder($cartId: ID!, $paymentInfo: PaymentInfoInput!, $voucherCode: String) {
              payForRetailOrder (cartId: $cartId, paymentInfo: $paymentInfo, voucherCode: $voucherCode) {
                order { id },
                paymentDetails {
                    clientSecret,
                    requiresAction
                }
              }
            }
          `,
          variables: {
            cartId: this.cartId,
            paymentInfo: {
              paymentMethodId: result.paymentMethod.id,
              address: this.address
            },
            voucherCode: this.voucherCode || null
          }
        })

        const paymentDetails = response.data.payForRetailOrder.paymentDetails
        if (paymentDetails.requiresAction) {
          // Request authentication
          this.handleAction(paymentDetails.clientSecret)
        } else {
          this.orderComplete(paymentDetails.clientSecret, response.data.payForRetailOrder.order.id)
        }
      } catch (error) {
        // TODO: Tidy this code
        if (error.networkError != null) {
          const networkError = error.networkError
          if (networkError.result != null && networkError.result.errors != null) {
            try {
              this.showError(networkError.result.errors.map(e => e.message.slice(e.message.lastIndexOf(';') + 2)).join('\n'))
            } catch {
              this.showError(networkError.result.errors.map(e => e.message).join('\n'))
            }
          } else {
            this.showError(networkError.message)
          }
        } else if (error.graphQLErrors != null) {
          this.showError(error.graphQLErrors[0].message)
        } else {
          this.showError(error.message)
        }
      }
    }
  },
  created () {
    // eslint-disable-next-line no-undef
    this.card = elements.create('card', {
      style: {
        base: {
          color: '#32325d',
          lineHeight: '24px',
          fontFamily: 'Raleway, sans-serif',
          // fontSmoothing: 'antialiased',
          fontSize: '18px',
          '::placeholder': {
            color: '#aab7c4'
          }
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a'
        }
      },
      hidePostalCode: true
    })
    this.address = { ...store.getters['auth/getAccountAddress'] }
  },
  beforeDestroy () {
    this.card.destroy()
  }
}
</script>
